import React from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import Button from './Button';

import './SmartRangeInput.css';
import t from '../i18n/I18n';

type Props = {
  value: number,
  min: number,
  max: number,
  step: number,
  baseline: number,
  style: Object,
  onChange: Function
};

export default class SmartRangeInput extends React.Component<Props> {
  static defaultProps = {
    min: 0,
    max: 10,
    baseline: 1,
    style: {}
  };

  _prevX = 0;

  componentDidMount() {
    this._handleOffset = this._handle.getBoundingClientRect().width / 2;
  }

  addListeners = () => {
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  };

  removeListeners = () => {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  };

  onMouseDown = (e) => {
    this.addListeners();
    this._prevX = e.pageX;
  };

  onMouseMove = (e) => {
    const rect = this._node.getBoundingClientRect();
    const pageX = e.pageX;
    if (pageX >= rect.left - this._handleOffset && pageX <= rect.right + this._handleOffset) {
      this.onChange(pageX);
    }
  };

  onMouseUp = () => {
    this.removeListeners();
    this._prevX = 0;
  };

  onSliderClick = (e) => {
    this.onChange(e.pageX);
  };

  onSliderTouch = (e) => {
    var touchobj = e.changedTouches[0];
    this.onChange(touchobj.pageX);
  };

  onChange = (pageX) => {
    const { value, min, max, step, baseline, onChange } = this.props;
    const rect = this._node.getBoundingClientRect();
    const x = pageX - rect.left;
    const useSmallScale = x < 1 / 3 * rect.width;
    const converter = this.getConverter(useSmallScale);
    const newValue = useSmallScale ? converter * x + min : converter * (x - rect.width / 3) + baseline;

    if (newValue >= min && newValue <= max && (!step || Math.abs(newValue - value) >= step)) {
      onChange(newValue);
    }
  };

  onResetClick = () => {
    const { baseline, onChange } = this.props;
    onChange(baseline);
  };

  getConverter = (useSmallScale) => {
    if (!this._node) {
      return 1;
    }

    const { min, max, value, baseline } = this.props;
    const rect = this._node.getBoundingClientRect();

    if (useSmallScale === undefined) {
      useSmallScale = value < baseline;
    }

    return useSmallScale ? (baseline - min) / (1 / 3 * rect.width) : (max - baseline) / (2 / 3 * rect.width);
  };

  getLeft = () => {
    const { min, value, baseline } = this.props;
    const converter = this.getConverter();
    let left, offset;
    if (value < baseline) {
      left = (value - min) / converter;
      offset = 0;
    } else {
      const width = this._node ? this._node.getBoundingClientRect().width : 0;
      left = (value - baseline) / converter;
      offset = 1 / 3 * width;
    }

    return `${left + offset}px`;
  };

  renderReset = () => {
    const { value, baseline } = this.props;
    if (value === baseline) {
      return null;
    }

    return (
      <Button className="reset" onClick={this.onResetClick}>
        {t('RESET')}
      </Button>
    );
  };

  render() {
    const { value, baseline, style } = this.props;

    return (
      <div
        name="SmartRangeInput"
        className={classNames({ modified: value !== baseline })}
        style={style}
        ref={(node) => (this._node = node)}
        onClick={this.onSliderClick}
        onTouchStart={this.onSliderTouch}
        onTouchMove={this.onSliderTouch}
        onTouchEnd={this.onSliderTouch}>
        <div className="track" />
        <div
          className="handle"
          onMouseDown={this.onMouseDown}
          style={{ left: this.getLeft() }}
          ref={(node) => (this._handle = node)}
        />
        {this.renderReset()}
      </div>
    );
  }
}
